import {
  Files,
  Question,
  SignOut,
  CaretDown,
  ArrowSquareOut,
  Sparkle,
} from "@phosphor-icons/react";
import classNames from "classnames";
import * as React from "react";
import styles from "./Navbar.module.css";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import * as Analytics from "~/hooks/useAnalytics";
import { useClerkAuthEnabled } from "~/hooks/useAuthenticated";
import { useLogout } from "~/hooks/useLogout";
import * as paths from "~/paths";

export interface ProfileDropdownProps {
  nimbusUrl?: string;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  nimbusUrl,
}) => {
  const { identity } = React.useContext(AuthenticationContext);
  const stubs = React.useContext(ReactRouterStubsContext);
  const { trackButton } = Analytics.useAnalytics();
  const isClerkAuthEnabled = useClerkAuthEnabled();
  const { isLoggingOut, handleLogoutSubmit } = useLogout();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const initials = React.useMemo(() => {
    if (!identity) return "U";

    // Handle null/undefined name
    const name = identity.name?.trim();
    if (name) {
      const nameParts = name
        .split(/\s+/)
        .filter((part) => part.length > 0)
        .map((part) => part[0]);

      if (nameParts.length > 0) {
        return nameParts.join("").toUpperCase().slice(0, 2);
      }
    }

    // Fallback to email initial
    return identity.userEmail?.[0]?.toUpperCase() || "U";
  }, [identity]);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  if (!identity) {
    return null;
  }

  const renderAvatar = (size: "small" | "large") => {
    const sizeClass =
      size === "small" ? styles.profileAvatar : styles.profileDropdownAvatar;
    const placeholderClass =
      size === "small"
        ? styles.profileAvatarPlaceholder
        : styles.profileDropdownAvatarPlaceholder;

    return identity.profileImage ? (
      <img
        src={identity.profileImage}
        alt={identity.name}
        className={sizeClass}
      />
    ) : (
      <span className={placeholderClass}>{initials}</span>
    );
  };

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    trackButton({
      element_text: `Toggle user menu ${newState ? "open" : "closed"}`,
      element_location: "Site desktop navigation",
    });
  };

  return (
    <div ref={dropdownRef} className={styles.profileDropdownContainer}>
      <button
        type="button"
        className={styles.profileTrigger}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Open profile menu"
      >
        {renderAvatar("small")}
        <CaretDown
          size={14}
          weight="bold"
          className={classNames(
            styles.profileChevron,
            isOpen && styles.profileChevronOpen,
          )}
        />
      </button>

      {isOpen && (
        <div className={styles.profileDropdown}>
          <div className={styles.profileDropdownHeader}>
            <div className={styles.profileDropdownUserInfo}>
              {renderAvatar("large")}
              <div className={styles.profileDropdownUserText}>
                <span className={styles.profileDropdownName}>
                  {identity.name}
                </span>
                <span className={styles.profileDropdownEmail}>
                  {identity.userEmail}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.profileDropdownDivider} />

          <div className={styles.profileDropdownItems}>
            <stubs.Link
              to={
                isClerkAuthEnabled ? paths.routes.priceReports() : "/requests"
              }
              className={styles.profileDropdownItem}
              onClick={() => {
                setIsOpen(false);
                trackButton({
                  element_text: "My requests",
                  element_location: "Site desktop navigation",
                });
              }}
              prefetch="intent"
            >
              <span className={styles.profileDropdownItemIcon}>
                <Files size={18} weight="regular" />
              </span>
              <span className={styles.profileDropdownItemText}>
                My requests
              </span>
            </stubs.Link>

            <stubs.Link
              to="https://help.vendr.com/en/articles/8365011-get-help-from-vendr"
              className={styles.profileDropdownItem}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setIsOpen(false);
                trackButton({
                  element_text: "Help",
                  element_location: "Site desktop navigation",
                });
              }}
            >
              <span className={styles.profileDropdownItemIcon}>
                <Question size={18} weight="regular" />
              </span>
              <span className={styles.profileDropdownItemText}>Help</span>
            </stubs.Link>

            {nimbusUrl && (
              <stubs.Link
                to={nimbusUrl}
                className={classNames(
                  styles.profileDropdownItem,
                  styles.profileDropdownItemRuth,
                )}
                onClick={() => {
                  setIsOpen(false);
                  trackButton({
                    element_text: "Chat with Ruth",
                    element_location: "Site desktop navigation",
                  });
                }}
              >
                <span className={styles.profileDropdownItemIcon}>
                  <Sparkle size={18} weight="regular" />
                </span>
                <span className={styles.profileDropdownItemText}>
                  Chat with Ruth
                  <ArrowSquareOut size={12} weight="bold" />
                </span>
              </stubs.Link>
            )}
          </div>

          <div className={styles.profileDropdownDivider} />

          <div className={styles.profileDropdownItems}>
            <stubs.Form
              method="post"
              action={paths.logout({ redirectBackTo: stubs.location })}
              onSubmit={handleLogoutSubmit}
              className={styles.profileDropdownLogoutForm}
            >
              <button
                type="submit"
                className={classNames(
                  styles.profileDropdownItem,
                  styles.profileDropdownItemLogout,
                )}
                disabled={isLoggingOut}
                onClick={() => {
                  trackButton({
                    element_text: "Log out",
                    element_location: "Site desktop navigation",
                  });
                }}
              >
                <span className={styles.profileDropdownItemIcon}>
                  <SignOut size={18} weight="regular" />
                </span>
                <span className={styles.profileDropdownItemText}>
                  {isLoggingOut ? "Logging out..." : "Log out"}
                </span>
              </button>
            </stubs.Form>
          </div>
        </div>
      )}
    </div>
  );
};
